"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useId, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { Tooltip } from "react-tooltip";
import { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { MemesSubmissionAdditionalInfoKey } from "@/components/waves/memes/submission/types/OperationalData";
import CopyIcon from "@/components/utils/icons/CopyIcon";
import { useAuth } from "@/components/auth/Auth";
interface AirdropEntry {
  readonly address: string;
  readonly count: number;
}
interface AllowlistBatch {
  readonly contract: string;
  readonly tokenIds: string;
}
function isPresent<T>(value: T | null): value is T {
  return value !== null;
}
const parseJson = <T,>(raw: string | undefined, fallback: T): T => {
  if (!raw) {
    return fallback;
  }
  try {
    const parsed = JSON.parse(raw) as T;
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};
const parseAirdropConfig = (raw: string | undefined): AirdropEntry[] => {
  const parsed = parseJson<unknown>(raw, []);
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed.map(entry => {
    const record = entry as {
      address?: unknown;
      count?: unknown;
    };
    const address = typeof record?.address === "string" ? record.address.trim() : "";
    const countValue = typeof record?.count === "number" ? record.count : Number(record?.count);
    if (!address || !Number.isFinite(countValue) || countValue <= 0) {
      return null;
    }
    return {
      address,
      count: countValue
    };
  }).filter(isPresent);
};
const parsePaymentAddress = (raw: string | undefined): string => {
  const parsed = parseJson<unknown>(raw, null);
  if (!parsed || typeof parsed !== "object") {
    return "";
  }
  const paymentAddress = (parsed as {
    payment_address?: unknown;
  }).payment_address;
  return typeof paymentAddress === "string" ? paymentAddress.trim() : "";
};
const parseAllowlistBatches = (raw: string | undefined): AllowlistBatch[] => {
  const parsed = parseJson<unknown>(raw, []);
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed.map(batch => {
    const record = batch as {
      contract?: unknown;
      token_ids?: unknown;
      token_ids_raw?: unknown;
    };
    const contract = typeof record?.contract === "string" ? record.contract.trim() : "";
    let tokenIds = "";
    if (typeof record?.token_ids === "string") {
      tokenIds = record.token_ids.trim();
    } else if (Array.isArray(record?.token_ids)) {
      tokenIds = record.token_ids.join(", ");
    } else if (typeof record?.token_ids_raw === "string") {
      tokenIds = record.token_ids_raw.trim();
    }
    if (!contract) {
      return null;
    }
    return {
      contract,
      tokenIds
    };
  }).filter(isPresent);
};
interface SingleWaveDropTechnicalDetailsProps {
  readonly drop: ExtendedDrop;
}
export const SingleWaveDropTechnicalDetails = t0 => {
  const $ = _c(54);
  const {
    drop
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const {
    setToast
  } = useAuth();
  const tooltipId = `technical-copy-${drop.id}`;
  let t1;
  if ($[0] !== setToast) {
    t1 = value => {
      if (!value) {
        return;
      }
      const writeText = navigator?.clipboard?.writeText;
      if (!writeText) {
        setToast({
          message: "Unable to copy to clipboard.",
          type: "error"
        });
        return;
      }
      writeText.call(navigator.clipboard, value).then(() => {
        setToast({
          message: "Copied to clipboard.",
          type: "success"
        });
      }).catch(() => {
        setToast({
          message: "Unable to copy to clipboard.",
          type: "error"
        });
      });
    };
    $[0] = setToast;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const copyToClipboard = t1;
  let t2;
  if ($[2] !== drop.metadata) {
    t2 = drop.metadata ?? [];
    $[2] = drop.metadata;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const metadata = t2;
  let t3;
  if ($[4] !== metadata) {
    t3 = key => metadata.find(item => item.data_key === key)?.data_value;
    $[4] = metadata;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const getEntryValue = t3;
  let t4;
  if ($[6] !== getEntryValue) {
    t4 = parsePaymentAddress(getEntryValue(MemesSubmissionAdditionalInfoKey.PAYMENT_INFO));
    $[6] = getEntryValue;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== getEntryValue) {
    t5 = parseAirdropConfig(getEntryValue(MemesSubmissionAdditionalInfoKey.AIRDROP_CONFIG));
    $[8] = getEntryValue;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== getEntryValue) {
    t6 = parseAllowlistBatches(getEntryValue(MemesSubmissionAdditionalInfoKey.ALLOWLIST_BATCHES));
    $[10] = getEntryValue;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = {
      paymentAddress: t4,
      airdropEntries: t5,
      allowlistBatches: t6
    };
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const {
    paymentAddress,
    airdropEntries,
    allowlistBatches
  } = t7;
  const hasDetails = Boolean(paymentAddress) || airdropEntries.length > 0 || allowlistBatches.length > 0;
  const panelId = useId();
  const buttonId = `${panelId}-toggle`;
  if (!hasDetails) {
    return null;
  }
  let t8;
  if ($[16] !== isOpen) {
    t8 = () => setIsOpen(!isOpen);
    $[16] = isOpen;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = `tw-w-full tw-px-4 tw-py-4 tw-flex tw-items-center tw-justify-between tw-text-left desktop-hover:hover:tw-bg-iron-900 tw-transition-colors tw-duration-300 tw-ease-out tw-border-0 ${isOpen ? "tw-bg-iron-800" : "tw-bg-iron-950"}`;
  const t10 = `tw-text-sm tw-font-medium ${isOpen ? "tw-text-iron-300" : "tw-text-iron-400"}`;
  let t11;
  if ($[18] !== t10) {
    t11 = <span className={t10}>Technical details</span>;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = isOpen ? 180 : 0;
  let t13;
  if ($[20] !== t12) {
    t13 = {
      rotate: t12
    };
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      duration: 0.3
    };
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = `tw-w-4 tw-h-4 tw-flex-shrink-0 ${isOpen ? "tw-text-iron-400" : "tw-text-iron-600"}`;
  let t16;
  if ($[23] !== t15) {
    t16 = <ChevronDownIcon className={t15} />;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t13 || $[26] !== t16) {
    t17 = <motion.div animate={t13} transition={t14}>{t16}</motion.div>;
    $[25] = t13;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== buttonId || $[29] !== isOpen || $[30] !== panelId || $[31] !== t11 || $[32] !== t17 || $[33] !== t8 || $[34] !== t9) {
    t18 = <button onClick={t8} id={buttonId} aria-expanded={isOpen} aria-controls={panelId} className={t9}>{t11}{t17}</button>;
    $[28] = buttonId;
    $[29] = isOpen;
    $[30] = panelId;
    $[31] = t11;
    $[32] = t17;
    $[33] = t8;
    $[34] = t9;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== airdropEntries || $[37] !== allowlistBatches || $[38] !== buttonId || $[39] !== copyToClipboard || $[40] !== isOpen || $[41] !== panelId || $[42] !== paymentAddress || $[43] !== tooltipId) {
    t19 = isOpen && <motion.div id={panelId} role="region" aria-labelledby={buttonId} initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="tw-overflow-hidden"><div className="tw-space-y-6 tw-text-xs tw-px-4 tw-py-4 tw-bg-iron-950">{paymentAddress && <div className="tw-p-4 tw-bg-iron-900/40 tw-rounded-lg tw-border tw-border-solid tw-border-iron-800"><div className="tw-text-[10px] tw-text-iron-500 tw-uppercase tw-tracking-wide tw-mb-3">Payment Address</div><div className="tw-py-2 tw-px-3 tw-bg-iron-900 tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-font-mono tw-text-iron-200 tw-font-medium tw-flex tw-items-center tw-justify-between tw-gap-2"><div className="tw-break-all">{paymentAddress}</div><button onClick={() => copyToClipboard(paymentAddress)} className="tw-shrink-0 tw-p-1.5 tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-0 tw-rounded tw-text-iron-400 hover:tw-text-iron-200 hover:tw-bg-iron-800/60 tw-transition-colors" title="Copy address" data-tooltip-id={tooltipId} data-tooltip-content="Copy"><span className="tw-scale-75 tw-inline-flex"><CopyIcon /></span></button></div></div>}{airdropEntries.length > 0 && <div className="tw-p-4 tw-bg-iron-900/40 tw-rounded-lg tw-border tw-border-solid tw-border-iron-900"><div className="tw-text-[10px] tw-text-iron-500 tw-uppercase tw-tracking-wide tw-mb-3">Airdrop</div><div className="tw-bg-iron-900 tw-rounded-lg tw-border tw-border-solid tw-border-iron-900 tw-divide-y tw-divide-iron-800 tw-divide-solid tw-divide-x-0">{airdropEntries.map((entry, index) => <div key={`${entry.address}-${index}`} className="tw-py-3 tw-px-3 tw-flex tw-items-center tw-justify-between tw-gap-3"><div className="tw-flex-1 tw-min-w-0 tw-font-mono tw-line-clamp-1 tw-text-iron-200 tw-font-medium tw-text-xs tw-break-all">{entry.address}</div><div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0"><div className="tw-text-iron-400 tw-font-medium">×{entry.count}</div><button onClick={() => copyToClipboard(entry.address)} className="tw-shrink-0 tw-p-1.5 tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-0 tw-rounded tw-text-iron-400 hover:tw-text-iron-200 hover:tw-bg-iron-800/60 tw-transition-colors" title="Copy address" data-tooltip-id={tooltipId} data-tooltip-content="Copy"><span className="tw-scale-75 tw-inline-flex"><CopyIcon /></span></button></div></div>)}</div></div>}{allowlistBatches.length > 0 && <div className="tw-p-4 tw-bg-iron-900/40 tw-border tw-border-solid tw-border-iron-900"><div className="tw-text-[10px] tw-text-iron-500 tw-uppercase tw-tracking-wide tw-mb-3">Allowlist</div><div className="tw-space-y-3">{allowlistBatches.map((batch, index_0) => <div key={`${batch.contract}-${index_0}`} className="tw-p-3 tw-bg-iron-900 tw-rounded-lg tw-space-y-2"><div><div className="tw-text-[10px] tw-text-iron-500">Contract Address</div><div className="tw-flex tw-items-center tw-justify-between tw-gap-2"><div className="tw-font-mono tw-line-clamp-1 tw-text-iron-200 tw-font-medium tw-text-xs tw-break-all">{batch.contract}</div><button onClick={() => copyToClipboard(batch.contract)} className="tw-shrink-0 tw-p-1.5 tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-0 tw-rounded tw-text-iron-400 hover:tw-text-iron-200 hover:tw-bg-iron-800/60 tw-transition-colors" title="Copy address" data-tooltip-id={tooltipId} data-tooltip-content="Copy"><span className="tw-scale-75 tw-inline-flex"><CopyIcon /></span></button></div></div>{batch.tokenIds && <div className="tw-pt-2 tw-border-t tw-border-iron-800 tw-border-solid tw-border-x-0 tw-border-b-0"><div className="tw-text-[10px] tw-text-iron-500 tw-mb-2">Token IDs</div><div className="tw-font-mono tw-text-iron-200 tw-font-medium tw-text-xs">{batch.tokenIds}</div></div>}</div>)}</div></div>}</div></motion.div>;
    $[36] = airdropEntries;
    $[37] = allowlistBatches;
    $[38] = buttonId;
    $[39] = copyToClipboard;
    $[40] = isOpen;
    $[41] = panelId;
    $[42] = paymentAddress;
    $[43] = tooltipId;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t19) {
    t20 = <AnimatePresence>{t19}</AnimatePresence>;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      background: "#37373E",
      color: "white",
      padding: "4px 8px",
      fontSize: "12px",
      fontWeight: 500,
      borderRadius: "6px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      zIndex: 99999,
      pointerEvents: "none"
    };
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== tooltipId) {
    t22 = <Tooltip id={tooltipId} place="top" offset={8} opacity={1} positionStrategy="fixed" style={t21} />;
    $[48] = tooltipId;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t18 || $[51] !== t20 || $[52] !== t22) {
    t23 = <div>{t18}{t20}{t22}</div>;
    $[50] = t18;
    $[51] = t20;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  return t23;
};